// Vista Tablet para Cocina - Gestión de Tareas Operativas
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getAuthToken } from '../api/api';
import { Calendar, ChevronLeft, ChevronRight, CheckSquare, Square, Clock, Users, AlertCircle, Play, StopCircle, UserPlus, Settings, Menu, X, BarChart3, LogIn, LogOut, ChevronDown, ChevronUp, Layers, Bell, RefreshCw, MapPin, Truck, UtensilsCrossed, Info } from 'lucide-react';

// Clave de un evento en la tablet: el presupuesto o, en multievento, el
// presupuesto y la jornada (misma clave que `eventos_info` del servidor)
const claveEventoTablet = (tarea) => (
  tarea?.jornada_id ? `${tarea.presupuesto_id}__${tarea.jornada_id}` : tarea?.presupuesto_id
);

// Preparaciones unificadas (U-65): completa tareas OPERATIVAS (no las genéricas
// de /tareas) con toggle-estado y estado=completada (no devuelve a pendiente
// una que ya estuviera hecha). Comprueba cada respuesta: devuelve cuántas han
// ido bien y cuántas no.
const completarTareasOperativas = async (lista) => {
  let ok = 0;
  let fallos = 0;
  for (const tarea of lista) {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tareas-operativas/${tarea.id}/toggle-estado?estado=completada`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (res.ok) {
        ok += 1;
      } else {
        fallos += 1;
      }
    } catch (e) {
      console.error('Error completando tarea:', e);
      fallos += 1;
    }
  }
  return { ok, fallos };
};

const TabletCocina = () => {
  const [tareas, setTareas] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('hoy'); // 'hoy', 'manana', 'semana'
  const [resumenCarga, setResumenCarga] = useState({});
  const [avisos, setAvisos] = useState([]);
  const [tiemposRestantes, setTiemposRestantes] = useState({});
  
  // Estados para modales
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const [showCambiarModal, setShowCambiarModal] = useState(false);
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);
  const [personaACambiar, setPersonaACambiar] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  
  // Estados para menú lateral y secciones
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [seccionActual, setSeccionActual] = useState('tareas'); // 'tareas', 'resumen', 'fichaje'
  const [mostrarCompletadas, setMostrarCompletadas] = useState(false);
  const [eventoSeleccionado, setEventoSeleccionado] = useState('todos'); // 'todos' o clave del evento (presupuesto o presupuesto__jornada)
  
  // Estados para planificación del día
  const [personasTrabajando, setPersonasTrabajando] = useState(2);
  const [horaSalida, setHoraSalida] = useState('12:00');
  const [panelPlanificacionExpandido, setPanelPlanificacionExpandido] = useState(false);
  
  // Estados para fichaje
  const [showModalEntrada, setShowModalEntrada] = useState(false);
  const [showModalSalida, setShowModalSalida] = useState(false);
  const [fichajesHoy, setFichajesHoy] = useState([]);

  // Estado para controlar alertas de sonido ya emitidas
  const [alertasEmitidas, setAlertasEmitidas] = useState(new Set());
  
  // Estado para indicar que hay cambios pendientes (en lugar de auto-refresh)
  const [hayCambiosPendientes, setHayCambiosPendientes] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date());
  
  // Estado para estadísticas de tiempos por empleado
  const [estadisticasEmpleados, setEstadisticasEmpleados] = useState({});
  const [tiempoPorDefecto, setTiempoPorDefecto] = useState(0);
  
  // Estado para controlar grupos de tareas expandidos
  const [gruposExpandidos, setGruposExpandidos] = useState(new Set());
  
  // Estado para info de eventos (desde backend)
  const [eventosInfo, setEventosInfo] = useState({});
  
  // Estado para controlar qué paneles de evento están expandidos
  const [eventoPanelExpandido, setEventoPanelExpandido] = useState(null);
  
  // Toggle para expandir/colapsar un grupo
  const toggleGrupoExpandido = (grupoKey) => {
    setGruposExpandidos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(grupoKey)) {
        newSet.delete(grupoKey);
      } else {
        newSet.add(grupoKey);
      }
      return newSet;
    });
  };

  // Define fetchEmpleados before using it in useEffect
  const fetchEmpleados = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/empleados-activos-simple`,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );
      const data = await response.json();
      setEmpleados(data.empleados || []);
    } catch (error) {
      console.error('Error fetching empleados:', error);
    }
  }, []);

  // Define fetchTareas before using it in useEffect
  const fetchTareas = useCallback(async () => {
    setLoading(true);
    try {
      let fechaDesde, fechaHasta;
      
      // Obtener fecha actual en hora local (no UTC)
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      

      if (viewMode === 'hoy') {
        // Para HOY: incluir tareas desde el pasado (atrasadas) hasta hoy
        const pastDate = new Date(today);
        pastDate.setDate(pastDate.getDate() - 30); // 30 días atrás
        const pastYear = pastDate.getFullYear();
        const pastMonth = String(pastDate.getMonth() + 1).padStart(2, '0');
        const pastDay = String(pastDate.getDate()).padStart(2, '0');
        
        fechaDesde = `${pastYear}-${pastMonth}-${pastDay}`;
        fechaHasta = todayStr;
      } else if (viewMode === 'manana') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowYear = tomorrow.getFullYear();
        const tomorrowMonth = String(tomorrow.getMonth() + 1).padStart(2, '0');
        const tomorrowDay = String(tomorrow.getDate()).padStart(2, '0');
        
        fechaDesde = `${tomorrowYear}-${tomorrowMonth}-${tomorrowDay}`;
        fechaHasta = fechaDesde;
      } else if (viewMode === 'semana') {
        fechaDesde = todayStr;
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        const weekYear = nextWeek.getFullYear();
        const weekMonth = String(nextWeek.getMonth() + 1).padStart(2, '0');
        const weekDay = String(nextWeek.getDate()).padStart(2, '0');
        fechaHasta = `${weekYear}-${weekMonth}-${weekDay}`;
      }

      // Agregar timestamp para forzar invalidación de caché
      const timestamp = new Date().getTime();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tareas-operativas?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}&_t=${timestamp}`,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );

      const data = await response.json();
      let tareasData = data.tareas || [];
      
      // DEBUG: Log para debugging
      if (tareasData.length > 0) {
        // Log de estados de todas las tareas
        const estadosCount = tareasData.reduce((acc, t) => {
          acc[t.estado] = (acc[t.estado] || 0) + 1;
          return acc;
        }, {});
      }
      
      // Ordenar: primero tareas atrasadas (fecha_prevista < hoy), luego tareas de hoy
      if (viewMode === 'hoy') {
        tareasData.sort((a, b) => {
          const fechaA = new Date(a.fecha_prevista);
          const fechaB = new Date(b.fecha_prevista);
          return fechaA - fechaB; // Orden ascendente (más antiguas primero)
        });
      }
      
      // NO filtrar completadas aquí - el filtro se hace en tareasAgrupadasGlobal
      // para permitir mostrar/ocultar completadas con el toggle
      
      
      setTareas(tareasData);
      setResumenCarga(data.resumen_carga || {});
      setAvisos(data.avisos || []);
      setEventosInfo(data.eventos_info || {});
      setUltimaActualizacion(new Date());
      setHayCambiosPendientes(false);
    } catch (error) {
      console.error('Error fetching tareas:', error);
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  // Define fetchFichajesHoy before using it in useEffect
  const fetchFichajesHoy = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/fichaje/hoy`,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        }
      );
      const data = await response.json();
      setFichajesHoy(data.registros || []);
    } catch (error) {
      console.error('Error fetching fichajes:', error);
    }
  }, []);

  useEffect(() => {
    fetchTareas();
    fetchEmpleados();
  }, [selectedDate, viewMode, fetchTareas, fetchEmpleados]);

  // Verificar si hay cambios cada 60 segundos (sin recargar automáticamente)
  useEffect(() => {
    const checkForChanges = async () => {
      try {
        const token = getAuthToken();
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        
        // Hacer una petición ligera para verificar si hay cambios
        const res = await fetch(`${API_URL}/api/tareas-operativas?estado=pendiente`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        
        // Comparar cantidad de tareas pendientes como indicador simple de cambios
        const tareasPendientesActuales = tareas.filter(t => t.estado === 'pendiente').length;
        const tareasPendientesNuevas = (data.tareas || []).length;
        
        if (tareasPendientesNuevas !== tareasPendientesActuales) {
          setHayCambiosPendientes(true);
        }
      } catch (e) {
      }
    };
    
    const interval = setInterval(checkForChanges, 60000); // Verificar cada 60 segundos
    return () => clearInterval(interval);
  }, [tareas]);

  // Función para actualizar manualmente
  const actualizarManualmente = () => {
    fetchTareas();
    setHayCambiosPendientes(false);
    setUltimaActualizacion(new Date());
  };

  useEffect(() => {
    if (seccionActual === 'fichaje') {
      fetchFichajesHoy();
    }
  }, [seccionActual, fetchFichajesHoy]);

  // Función para reproducir sonido de alerta suave
  const playAlertSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Frecuencia suave
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
    }
  };

  // Actualizar contadores cada segundo y emitir alertas
  useEffect(() => {
    const updateTiemposRestantes = () => {
      const nuevos = {};
      const nuevasAlertas = new Set(alertasEmitidas);
      
      tareas.forEach(tarea => {
        if (tarea.estado === 'en_proceso' && tarea.tiempo_inicio_global) {
          const inicio = new Date(tarea.tiempo_inicio_global);
          const ahora = new Date();
          const transcurrido = Math.floor((ahora - inicio) / 1000 / 60); // minutos transcurridos
          const restante = tarea.tiempo_estimado_min - transcurrido; // tiempo que queda
          const excedido = restante < 0;
          
          nuevos[tarea.id] = {
            transcurrido,
            restante: restante,
            excedido: excedido
          };
          
          // Emitir sonido cuando se excede por primera vez
          if (excedido && !alertasEmitidas.has(tarea.id)) {
            playAlertSound();
            nuevasAlertas.add(tarea.id);
            setAlertasEmitidas(nuevasAlertas);
          }
        }
      });
      setTiemposRestantes(nuevos);
    };

    // Actualizar inmediatamente
    updateTiemposRestantes();
    
    // Actualizar cada segundo
    const interval = setInterval(updateTiemposRestantes, 1000);
    
    return () => clearInterval(interval);
  }, [tareas, alertasEmitidas]);

  // Funciones helper para colores
  const getColorClasses = (color) => {
    const colorMap = {
      'amarillo': {
        bg: 'bg-yellow-300',
        bgLight: 'bg-yellow-50',
        border: 'border-yellow-400',
        text: 'text-yellow-900',
        hover: 'hover:bg-yellow-400'
      },
      'azul': {
        bg: 'bg-blue-400',
        bgLight: 'bg-blue-50',
        border: 'border-blue-400',
        text: 'text-blue-900',
        hover: 'hover:bg-blue-500'
      },
      'verde': {
        bg: 'bg-green-400',
        bgLight: 'bg-green-50',
        border: 'border-green-400',
        text: 'text-green-900',
        hover: 'hover:bg-green-500'
      },
      'naranja': {
        bg: 'bg-orange-400',
        bgLight: 'bg-orange-50',
        border: 'border-orange-400',
        text: 'text-orange-900',
        hover: 'hover:bg-orange-500'
      },
      'morado': {
        bg: 'bg-purple-400',
        bgLight: 'bg-purple-50',
        border: 'border-purple-400',
        text: 'text-purple-900',
        hover: 'hover:bg-purple-500'
      },
      'rosa': {
        bg: 'bg-pink-400',
        bgLight: 'bg-pink-50',
        border: 'border-pink-400',
        text: 'text-pink-900',
        hover: 'hover:bg-pink-500'
      },
      'rojo': {
        bg: 'bg-red-400',
        bgLight: 'bg-red-50',
        border: 'border-red-400',
        text: 'text-red-900',
        hover: 'hover:bg-red-500'
      },
      'cyan': {
        bg: 'bg-cyan-400',
        bgLight: 'bg-cyan-50',
        border: 'border-cyan-400',
        text: 'text-cyan-900',
        hover: 'hover:bg-cyan-500'
      }
    };
    return colorMap[color] || colorMap['azul'];
  };

  const getMixedBackground = (personas) => {
    if (!personas || personas.length === 0) return 'bg-white';
    if (personas.length === 1) {
      const color = personas[0].color;
      return color ? getColorClasses(color).bgLight : 'bg-white';
    }
    // Múltiples personas: crear gradient
    const colores = personas.map(p => {
      const colorClass = p.color ? getColorClasses(p.color).bgLight : 'bg-gray-50';
      return colorClass.replace('bg-', '');
    });
    return `bg-gradient-to-r from-${colores[0]} via-${colores[1] || colores[0]} to-${colores[colores.length - 1]}`;
  };

  // Cargar estadísticas de tiempos por empleado para una tarea
  const fetchEstadisticasEmpleados = async (nombreTarea) => {
    try {
      const token = getAuthToken();
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      
      const res = await fetch(
        `${API_URL}/api/tareas-operativas/estadisticas-empleados?nombre_tarea=${encodeURIComponent(nombreTarea)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const data = await res.json();
      
      if (data.success) {
        // Convertir array a objeto por empleado_id para acceso rápido
        const statsMap = {};
        data.estadisticas.forEach(stat => {
          statsMap[stat.empleado_id] = stat;
        });
        setEstadisticasEmpleados(statsMap);
        setTiempoPorDefecto(data.tiempo_por_defecto_lote || 0);
      }
    } catch (e) {
      console.error('Error cargando estadísticas:', e);
      setEstadisticasEmpleados({});
    }
  };

  const handleAbrirModalAsignar = async (tarea) => {
    // Primero limpiar estados anteriores
    setEstadisticasEmpleados({});
    setTiempoPorDefecto(0);
    setTareaSeleccionada(tarea);
    setShowAsignarModal(true);
    
    // Cargar estadísticas para esta tarea
    if (tarea?.nombre_tarea) {
      fetchEstadisticasEmpleados(tarea.nombre_tarea);
    }
  };

  const handleAsignarPersona = async (empleadoId, tareaIdOverride = null) => {
    const tareaId = tareaIdOverride || tareaSeleccionada?.id;
    if (!tareaId) return;
    
    try {
      const empleado = empleados.find(e => e.id === empleadoId);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tareas-operativas/${tareaId}/asignar-persona`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify({
            empleado_id: empleadoId
          })
        }
      );

      if (response.ok) {
        // Mostrar toast solo si es una asignación individual
        if (!tareaIdOverride) {
          const nombreEmpleado = (empleado?.nombre_completo || empleado?.nombre || 'Empleado').split(' ')[0];
          setToastMessage(`Tarea asignada a ${nombreEmpleado}`);
          setTimeout(() => setToastMessage(null), 2000);
          
          setShowAsignarModal(false);
          setTareaSeleccionada(null);
        }
        fetchTareas();
      } else {
        const error = await response.json();
        console.error(error.detail || 'Error al asignar persona');
      }
    } catch (error) {
      console.error('Error asignando persona:', error);
    }
  };

  const handleFinalizarPersona = async (tareaId, empleadoId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tareas-operativas/${tareaId}/finalizar-persona`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify({
            empleado_id: empleadoId
          })
        }
      );

      if (response.ok) {
        fetchTareas();
      }
    } catch (error) {
      console.error('Error finalizando persona:', error);
      alert('Error al finalizar tarea');
    }
  };

  const handleAbrirModalCambiar = (tarea, persona) => {
    setTareaSeleccionada(tarea);
    setPersonaACambiar(persona);
    setShowCambiarModal(true);
  };

  const handleCambiarPersona = async (nuevoEmpleadoId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tareas-operativas/${tareaSeleccionada.id}/cambiar-persona`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify({
            empleado_id_anterior: personaACambiar.empleado_id,
            empleado_id_nuevo: nuevoEmpleadoId
          })
        }
      );

      if (response.ok) {
        setShowCambiarModal(false);
        setTareaSeleccionada(null);
        setPersonaACambiar(null);
        fetchTareas();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al cambiar persona');
      }
    } catch (error) {
      console.error('Error cambiando persona:', error);
      alert('Error al cambiar persona');
    }
  };

  // Funciones de Fichaje
  const handleFicharEntrada = async (empleadoId) => {
    try {
      const empleado = empleados.find(e => e.id === empleadoId);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/fichaje/entrada`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify({ empleado_id: empleadoId })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setToastMessage(data.mensaje);
        setTimeout(() => setToastMessage(null), 2000);
        setShowModalEntrada(false);
        fetchFichajesHoy();
      } else {
        alert(data.detail || 'Error al registrar entrada');
      }
    } catch (error) {
      console.error('Error registrando entrada:', error);
      alert('Error al registrar entrada');
    }
  };

  const handleFicharSalida = async (empleadoId) => {
    try {
      const empleado = empleados.find(e => e.id === empleadoId);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/fichaje/salida`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify({ empleado_id: empleadoId })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setToastMessage(`${data.mensaje} (${data.horas_trabajadas}h trabajadas)`);
        setTimeout(() => setToastMessage(null), 3000);
        setShowModalSalida(false);
        fetchFichajesHoy();
      } else {
        alert(data.detail || 'Error al registrar salida');
      }
    } catch (error) {
      console.error('Error registrando salida:', error);
      alert('Error al registrar salida');
    }
  };

  // Filtrar empleados para modales de fichaje
  const getEmpleadosParaEntrada = () => {
    // Empleados que NO tienen entrada hoy o ya completaron su jornada (tienen entrada Y salida)
    return empleados.filter(emp => {
      const fichaje = fichajesHoy.find(f => f.empleado_id === emp.id);
      return !fichaje || (fichaje.hora_entrada && fichaje.hora_salida);
    });
  };

  const getEmpleadosParaSalida = () => {
    // Empleados que tienen entrada pero NO tienen salida
    return empleados.filter(emp => {
      const fichaje = fichajesHoy.find(f => f.empleado_id === emp.id);
      return fichaje && fichaje.hora_entrada && !fichaje.hora_salida;
    });
  };

  const getFaseLabel = (fase) => {
    const labels = {
      '-2': 'Preparación Material (–2 días)',
      '-1': 'Preproducción (–1 día)',
      '0': 'Producción Día Evento',
      '+1': 'Postevento (+1 día)'
    };
    return labels[fase] || fase;
  };

  const getFaseColor = (fase) => {
    const colors = {
      '-2': 'bg-blue-500',
      '-1': 'bg-orange-500',
      '0': 'bg-green-500',
      '+1': 'bg-gray-500'
    };
    return colors[fase] || 'bg-gray-500';
  };

  const getFaseBgColor = (fase) => {
    const colors = {
      '-2': 'bg-blue-50',
      '-1': 'bg-orange-50',
      '0': 'bg-green-50',
      '+1': 'bg-gray-50'
    };
    return colors[fase] || 'bg-gray-50';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Resetear horas para comparar solo fechas
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date.getTime() < today.getTime()) return '⚠️ ATRASADA';
    if (date.getTime() === today.getTime()) return 'HOY';
    if (date.getTime() === tomorrow.getTime()) return 'MAÑANA';

    return date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  // Agrupar tareas por fecha
  const tareasPorFecha = tareas.reduce((acc, tarea) => {
    const fecha = tarea.fecha_prevista.split('T')[0];
    if (!acc[fecha]) acc[fecha] = [];
    acc[fecha].push(tarea);
    return acc;
  }, {});

  // Obtener eventos únicos del período seleccionado. En multievento, un
  // evento por jornada (presupuesto + jornada) con los datos de ESA jornada
  const getEventosUnicos = () => {
    const eventosMap = new Map();

    tareas.forEach(tarea => {
      const clave = claveEventoTablet(tarea);
      if (!eventosMap.has(clave)) {
        const info = eventosInfo[clave] || eventosInfo[tarea.presupuesto_id] || {};
        eventosMap.set(clave, {
          clave,
          presupuesto_id: tarea.presupuesto_id,
          jornada_id: tarea.jornada_id || null,
          jornada_etiqueta: info.jornada_etiqueta || tarea.jornada_etiqueta || '',
          personas_desglose: info.personas_desglose || '',
          presupuesto_numero: tarea.presupuesto_numero,
          cliente_nombre: tarea.cliente_nombre,
          fecha_evento: info.fecha_evento || tarea.fecha_evento,
          hora_inicio: info.hora_inicio || '',
          hora_fin: info.hora_fin || '',
          hora_llegada_estimada: info.hora_llegada_estimada || '',
          num_personas: info.num_personas || 0,
          lugar_evento: info.lugar_evento || '',
          observaciones: info.observaciones || '',
          personal_asignado: info.personal_asignado || [],
          vehiculos_asignados: info.vehiculos_asignados || [],
          menus_nombres: info.menus_nombres || [],
          menus_detalle: info.menus_detalle || [],
          total_tareas: 1,
          completadas: tarea.estado === 'completada' ? 1 : 0
        });
      } else {
        const evento = eventosMap.get(clave);
        evento.total_tareas += 1;
        if (tarea.estado === 'completada') evento.completadas += 1;
      }
    });
    
    return Array.from(eventosMap.values()).sort((a, b) => 
      new Date(a.fecha_evento) - new Date(b.fecha_evento)
    );
  };

  const eventosUnicos = getEventosUnicos();

  // Filtrar tareas por evento seleccionado
  const tareasFiltradas = eventoSeleccionado === 'todos'
    ? tareas
    : tareas.filter(t => claveEventoTablet(t) === eventoSeleccionado);

  // Helper: formatear desglose de lotes (ej: "1x40 + 1x10")
  const formatDesgloseLotes = (tarea) => {
    const desglose = tarea.desglose_lotes;
    if (desglose && desglose.length > 0) {
      // Agrupar lotes iguales: ej [40,40,10] -> "2x40 + 1x10"
      const agrupado = {};
      desglose.forEach(l => {
        agrupado[l.unidades] = (agrupado[l.unidades] || 0) + 1;
      });
      return Object.entries(agrupado)
        .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
        .map(([uds, count]) => `${count}x${uds}`)
        .join(' + ');
    }
    // Fallback: calcular manualmente
    const ut = tarea.unidades_totales || 0;
    const lu = tarea.lote_unidades || 10;
    if (ut <= 0 || lu <= 0) return `${ut} uds`;
    const completos = Math.floor(ut / lu);
    const resto = ut % lu;
    const parts = [];
    if (completos > 0) parts.push(`${completos}x${lu}`);
    if (resto > 0) parts.push(`1x${resto}`);
    return parts.join(' + ');
  };

  // Helper: verificar si una tarea es atrasada
  const esAtrasada = (fechaPrevista) => {
    if (!fechaPrevista) return false;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fecha = new Date(fechaPrevista);
    fecha.setHours(0, 0, 0, 0);
    return fecha < hoy;
  };

  // Helper: verificar si una tarea es de hoy
  const esDeHoy = (fechaPrevista) => {
    if (!fechaPrevista) return false;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fecha = new Date(fechaPrevista);
    fecha.setHours(0, 0, 0, 0);
    return fecha.getTime() === hoy.getTime();
  };

  // AGRUPACIÓN GLOBAL: Agrupar TODAS las tareas iguales en UNA tarjeta (sin importar fecha)
  const tareasAgrupadasGlobal = useMemo(() => {
    // Filtrar completadas si no se quieren ver
    const tareasParaAgrupar = mostrarCompletadas 
      ? tareasFiltradas.filter(t => t.estado === 'completada')
      : tareasFiltradas.filter(t => t.estado !== 'completada');
    
    const grupos = {};
    
    tareasParaAgrupar.forEach(tarea => {
      const key = `${tarea.nombre_tarea}__${tarea.plato_id || 'sin_plato'}`;
      
      if (!grupos[key]) {
        grupos[key] = {
          key,
          nombre_tarea: tarea.nombre_tarea,
          plato_id: tarea.plato_id,
          plato_nombre: tarea.plato_nombre,
          lote_unidades: tarea.lote_unidades || 10,
          subtareas: [],
          unidades_totales: 0,
          lotes_totales: 0,
          tiempo_estimado_total: 0,
          completadas: 0,
          en_proceso: 0,
          pendientes: 0,
          atrasadas: 0,
          ingredientes_consolidados: {},
          materiales_consolidados: {}
        };
      }
      
      const g = grupos[key];
      const tareaAtrasada = esAtrasada(tarea.fecha_prevista);
      const tareaEsDeHoy = esDeHoy(tarea.fecha_prevista);
      const lotes = Math.ceil((tarea.unidades_totales || 0) / (tarea.lote_unidades || g.lote_unidades || 10));
      
      g.subtareas.push({
        ...tarea,
        esAtrasada: tareaAtrasada,
        esDeHoy: tareaEsDeHoy,
        esUrgente: tareaAtrasada || tareaEsDeHoy,
        lotes
      });
      
      g.unidades_totales += tarea.unidades_totales || 0;
      g.lotes_totales += lotes;
      g.tiempo_estimado_total += tarea.tiempo_estimado_min || 0;
      
      if (tarea.estado === 'completada') g.completadas++;
      else if (tarea.estado === 'en_proceso') g.en_proceso++;
      else g.pendientes++;
      
      if (tareaAtrasada && tarea.estado !== 'completada') g.atrasadas++;
      
      (tarea.ingredientes || []).forEach(ing => {
        const ingKey = ing.nombre;
        if (!g.ingredientes_consolidados[ingKey]) {
          g.ingredientes_consolidados[ingKey] = { ...ing, cantidad_total: 0 };
        }
        g.ingredientes_consolidados[ingKey].cantidad_total += ing.cantidad_total || 0;
      });
      
      (tarea.materiales || []).forEach(mat => {
        const matKey = mat.nombre;
        if (!g.materiales_consolidados[matKey]) {
          g.materiales_consolidados[matKey] = { ...mat, cantidad: 0 };
        }
        g.materiales_consolidados[matKey].cantidad += mat.cantidad || 0;
      });
    });
    
    return Object.values(grupos)
      .map(grupo => ({
        ...grupo,
        ingredientes: Object.values(grupo.ingredientes_consolidados),
        materiales: Object.values(grupo.materiales_consolidados),
        estadoGrupo: grupo.completadas === grupo.subtareas.length ? 'completada' 
          : grupo.en_proceso > 0 ? 'en_proceso' : 'pendiente',
        subtareas: grupo.subtareas.sort((a, b) => {
          if (a.esAtrasada && !b.esAtrasada) return -1;
          if (!a.esAtrasada && b.esAtrasada) return 1;
          if (a.esDeHoy && !b.esDeHoy) return -1;
          if (!a.esDeHoy && b.esDeHoy) return 1;
          return new Date(a.fecha_evento) - new Date(b.fecha_evento);
        })
      }))
      .sort((a, b) => {
        if (a.atrasadas > 0 && b.atrasadas === 0) return -1;
        if (a.atrasadas === 0 && b.atrasadas > 0) return 1;
        return 0;
      });
  }, [tareasFiltradas, mostrarCompletadas]);

  // Mantener compatibilidad con código existente (para otras secciones)
  const tareasFiltadasPorFecha = tareasFiltradas.reduce((acc, tarea) => {
    const fecha = tarea.fecha_prevista.split('T')[0];
    if (!acc[fecha]) acc[fecha] = [];
    acc[fecha].push(tarea);
    return acc;
  }, {});

  // Función legacy para compatibilidad
  const agruparTareasPorTipo = (tareasFase) => {
    const grupos = {};
    
    tareasFase.forEach(tarea => {
      const key = `${tarea.nombre_tarea}__${tarea.plato_id || 'sin_plato'}`;
      
      if (!grupos[key]) {
        grupos[key] = {
          key,
          nombre_tarea: tarea.nombre_tarea,
          plato_id: tarea.plato_id,
          plato_nombre: tarea.plato_nombre,
          responsable_sugerido: tarea.responsable_sugerido,
          lote_unidades: tarea.lote_unidades,
          subtareas: [],
          unidades_totales: 0,
          tiempo_estimado_total: 0,
          completadas: 0,
          en_proceso: 0,
          pendientes: 0,
          ingredientes_consolidados: {},
          materiales_consolidados: {}
        };
      }
      
      grupos[key].subtareas.push(tarea);
      grupos[key].unidades_totales += tarea.unidades_totales || 0;
      grupos[key].tiempo_estimado_total += tarea.tiempo_estimado_min || 0;
      
      if (tarea.estado === 'completada') grupos[key].completadas++;
      else if (tarea.estado === 'en_proceso') grupos[key].en_proceso++;
      else grupos[key].pendientes++;
      
      (tarea.ingredientes || []).forEach(ing => {
        const ingKey = ing.nombre;
        if (!grupos[key].ingredientes_consolidados[ingKey]) {
          grupos[key].ingredientes_consolidados[ingKey] = { ...ing, cantidad_total: 0 };
        }
        grupos[key].ingredientes_consolidados[ingKey].cantidad_total += ing.cantidad_total || 0;
      });
      
      (tarea.materiales || []).forEach(mat => {
        const matKey = mat.nombre;
        if (!grupos[key].materiales_consolidados[matKey]) {
          grupos[key].materiales_consolidados[matKey] = { ...mat, cantidad: 0 };
        }
        grupos[key].materiales_consolidados[matKey].cantidad += mat.cantidad || 0;
      });
    });
    
    return Object.values(grupos).map(grupo => ({
      ...grupo,
      ingredientes: Object.values(grupo.ingredientes_consolidados),
      materiales: Object.values(grupo.materiales_consolidados),
      // Determinar estado del grupo
      estadoGrupo: grupo.completadas === grupo.subtareas.length 
        ? 'completada' 
        : grupo.en_proceso > 0 
          ? 'en_proceso' 
          : 'pendiente'
    }));
  };

  // Calcular estadísticas por evento
  const getEstadisticasPorEvento = () => {
    const eventoStats = {};
    
    tareas.forEach(tarea => {
      // Multievento: una tarjeta por jornada
      const eventoKey = claveEventoTablet(tarea);

      if (!eventoStats[eventoKey]) {
        const info = eventosInfo[eventoKey] || eventosInfo[tarea.presupuesto_id] || {};
        eventoStats[eventoKey] = {
          clave: eventoKey,
          presupuesto_id: tarea.presupuesto_id,
          jornada_etiqueta: info.jornada_etiqueta || tarea.jornada_etiqueta || '',
          presupuesto_numero: tarea.presupuesto_numero,
          cliente_nombre: tarea.cliente_nombre,
          fecha_evento: info.fecha_evento || tarea.fecha_evento,
          hora_inicio: info.hora_inicio || '',
          hora_fin: info.hora_fin || '',
          num_personas: info.num_personas || 0,
          lugar_evento: info.lugar_evento || '',
          personal_asignado: info.personal_asignado || [],
          vehiculos_asignados: info.vehiculos_asignados || [],
          menus_nombres: info.menus_nombres || [],
          total: 0,
          completadas: 0,
          en_proceso: 0,
          pendientes: 0,
          tiempo_total_estimado: 0,
          tiempo_total_real: 0,
          tareas: []
        };
      }
      
      eventoStats[eventoKey].total += 1;
      eventoStats[eventoKey].tareas.push(tarea);
      eventoStats[eventoKey].tiempo_total_estimado += tarea.tiempo_estimado_min || 0;
      
      if (tarea.estado === 'completada') {
        eventoStats[eventoKey].completadas += 1;
        eventoStats[eventoKey].tiempo_total_real += tarea.tiempo_real_min || 0;
      } else if (tarea.estado === 'en_proceso') {
        eventoStats[eventoKey].en_proceso += 1;
      } else {
        eventoStats[eventoKey].pendientes += 1;
      }
    });
    
    return Object.values(eventoStats);
  };

  const estadisticasEventos = getEstadisticasPorEvento();

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Menú Lateral */}
      <div className={`fixed inset-y-0 left-0 w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
        menuAbierto ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          {/* Header del menú */}
          <div className="bg-gradient-to-r from-duetto-primary to-green-600 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Portal Cocina</h2>
              <button
                onClick={() => setMenuAbierto(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-green-100 text-sm">
              {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Opciones del menú */}
          <nav className="flex-1 p-4 space-y-2">
            <button
              onClick={() => {
                setSeccionActual('tareas');
                setMenuAbierto(false);
              }}
              className={`w-full flex items-center space-x-3 px-4 py-4 rounded-lg font-bold text-lg transition-all ${
                seccionActual === 'tareas'
                  ? 'bg-duetto-primary text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CheckSquare className="w-6 h-6" />
              <span>Tareas del Día</span>
            </button>

            <button
              onClick={() => {
                setSeccionActual('resumen');
                setMenuAbierto(false);
              }}
              className={`w-full flex items-center space-x-3 px-4 py-4 rounded-lg font-bold text-lg transition-all ${
                seccionActual === 'resumen'
                  ? 'bg-duetto-primary text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BarChart3 className="w-6 h-6" />
              <span>Resumen de Eventos</span>
            </button>

            <button
              onClick={() => {
                setSeccionActual('fichaje');
                setMenuAbierto(false);
              }}
              className={`w-full flex items-center space-x-3 px-4 py-4 rounded-lg font-bold text-lg transition-all ${
                seccionActual === 'fichaje'
                  ? 'bg-duetto-primary text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Clock className="w-6 h-6" />
              <span>Fichaje</span>
            </button>
          </nav>

          {/* Footer del menú */}
          <div className="p-4 border-t border-gray-200">
            <div className="text-sm text-gray-500 text-center">
              Versión 1.0 - Duetto Catering
            </div>
          </div>
        </div>
      </div>

      {/* Overlay cuando el menú está abierto */}
      {menuAbierto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMenuAbierto(false)}
        />
      )}

      {/* Contenido principal */}
      <div className="p-2 sm:p-4">
      {/* Header - Compacto */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 mb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setMenuAbierto(true)}
              className="bg-duetto-primary hover:bg-green-600 text-white rounded-lg p-2.5 transition-all shadow"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                {seccionActual === 'tareas' && (
                  <>
                    <Calendar className="w-6 h-6 mr-2 text-duetto-primary" />
                    Orden de Cocina
                  </>
                )}
                {seccionActual === 'resumen' && (
                  <>
                    <BarChart3 className="w-6 h-6 mr-2 text-duetto-primary" />
                    Resumen de Eventos
                  </>
                )}
                {seccionActual === 'fichaje' && (
                  <>
                    <Clock className="w-6 h-6 mr-2 text-duetto-primary" />
                    Fichaje de Horas
                  </>
                )}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hayCambiosPendientes && (
              <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1.5 rounded-lg animate-pulse">
                <Bell className="w-4 h-4" />
                <span className="text-xs font-medium hidden sm:inline">Cambios</span>
              </div>
            )}
            <button
              onClick={actualizarManualmente}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                hayCambiosPendientes 
                  ? 'bg-yellow-500 hover:bg-yellow-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualizar</span>
            </button>
            <div className="text-right text-xs text-gray-400">
              {ultimaActualizacion.toLocaleTimeString('es-ES', {hour:'2-digit', minute:'2-digit'})}
            </div>
          </div>
        </div>

        {/* Period selector - tablet-friendly large buttons */}
        {seccionActual === 'tareas' && (
        <div className="flex items-center gap-3 mb-3">
          {[
            { key: 'hoy', label: 'Hoy' },
            { key: 'manana', label: 'Mañana' },
            { key: 'semana', label: '7 días' }
          ].map(v => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key)}
              className={`px-6 py-3 rounded-xl text-base font-bold transition-all ${
                viewMode === v.key
                  ? 'bg-duetto-primary text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`view-mode-${v.key}`}
            >
              {v.label}
            </button>
          ))}
          
          <div className="flex-1" />
          
          {/* Toggle completadas inline */}
          <button
            onClick={() => setMostrarCompletadas(!mostrarCompletadas)}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl text-base font-medium transition-all ${
              mostrarCompletadas
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-gray-50 text-gray-500 border border-gray-200'
            }`}
            data-testid="toggle-completadas"
          >
            {mostrarCompletadas ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
            <span>Completadas</span>
          </button>
        </div>
        )}

        {/* Event chips - tablet-friendly touch targets */}
        {seccionActual === 'tareas' && eventosUnicos.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            <button
              onClick={() => setEventoSeleccionado('todos')}
              className={`flex-shrink-0 px-5 py-3 rounded-xl text-sm font-bold transition-all ${
                eventoSeleccionado === 'todos'
                  ? 'bg-gray-800 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="event-chip-todos"
            >
              Todos ({eventosUnicos.length})
            </button>
            {eventosUnicos.map(evento => {
              const progreso = evento.total_tareas > 0 
                ? Math.round((evento.completadas / evento.total_tareas) * 100) 
                : 0;
              const tiempoRestanteEvento = tareas
                .filter(t => claveEventoTablet(t) === evento.clave && t.estado !== 'completada')
                .reduce((sum, t) => sum + (t.tiempo_estimado_min || 0), 0);

              return (
                <button
                  key={evento.clave}
                  onClick={() => setEventoSeleccionado(evento.clave)}
                  className={`flex-shrink-0 px-5 py-3 rounded-xl text-sm font-bold transition-all ${
                    eventoSeleccionado === evento.clave
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-400'
                  }`}
                  data-testid={`event-chip-${evento.clave}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="max-w-[180px] truncate">{evento.cliente_nombre}</span>
                    {evento.jornada_etiqueta && (
                      <span className="text-xs font-normal opacity-80">{evento.jornada_etiqueta}</span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      progreso === 100 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {evento.completadas}/{evento.total_tareas}
                    </span>
                    {tiempoRestanteEvento > 0 && (
                      <span className="text-xs opacity-75">{tiempoRestanteEvento}m</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Panel Info Evento - visible al seleccionar un evento */}
      {seccionActual === 'tareas' && eventoSeleccionado !== 'todos' && (() => {
        const ev = eventosUnicos.find(e => e.clave === eventoSeleccionado);
        if (!ev) return null;
        const fechaEv = ev.fecha_evento ? new Date(ev.fecha_evento) : null;
        const fechaStr = fechaEv ? fechaEv.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
        
        return (
          <div className="bg-white border border-blue-200 rounded-xl p-3 mb-3 shadow-sm" data-testid="evento-info-panel">
            <div className="flex items-start gap-3">
              {/* Col izquierda: Info principal */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-bold text-gray-900 text-base">#{ev.presupuesto_numero}</span>
                  {ev.jornada_etiqueta && (
                    <span className="font-semibold text-blue-700 text-sm" data-testid="evento-jornada">{ev.jornada_etiqueta}</span>
                  )}
                  <span className="text-gray-700 text-sm truncate">{ev.cliente_nombre}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                  {fechaStr && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />{fechaStr}
                    </span>
                  )}
                  {ev.hora_inicio && (
                    <span className="flex items-center gap-1 font-semibold text-blue-700">
                      <Clock className="w-3 h-3" />{ev.hora_inicio}{ev.hora_fin ? ` - ${ev.hora_fin}` : ''}
                    </span>
                  )}
                  {ev.num_personas > 0 && (
                    <span className="flex items-center gap-1 font-semibold text-purple-700" title={ev.personas_desglose || undefined}>
                      <Users className="w-3 h-3" />{ev.num_personas} comensales
                      {ev.personas_desglose && <span className="font-normal text-gray-500">({ev.personas_desglose})</span>}
                    </span>
                  )}
                  {ev.lugar_evento && (
                    <span className="flex items-center gap-1 max-w-[250px] truncate" title={ev.lugar_evento}>
                      <MapPin className="w-3 h-3 flex-shrink-0" />{ev.lugar_evento}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Col derecha: Personal, Vehículo, Menús */}
              <div className="flex-shrink-0 flex flex-wrap gap-2 items-start max-w-[50%]">
                {ev.personal_asignado.length > 0 && (
                  <span className="flex items-center gap-1 bg-green-50 border border-green-200 text-green-800 text-xs px-2 py-1 rounded-lg" data-testid="evento-personal">
                    <UserPlus className="w-3 h-3" />
                    {ev.personal_asignado.map(p => p.nombre).join(', ')}
                  </span>
                )}
                {ev.vehiculos_asignados.length > 0 && (
                  <span className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-2 py-1 rounded-lg" data-testid="evento-vehiculo">
                    <Truck className="w-3 h-3" />
                    {ev.vehiculos_asignados.map(v => v.nombre || v.matricula).join(', ')}
                  </span>
                )}
                {ev.menus_detalle.length > 0 && (
                  <button 
                    onClick={() => setEventoPanelExpandido(eventoPanelExpandido === ev.clave ? null : ev.clave)}
                    className="flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 text-xs px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors"
                    data-testid="evento-menus-toggle"
                  >
                    <UtensilsCrossed className="w-3 h-3" />
                    {ev.menus_detalle.length} menú{ev.menus_detalle.length > 1 ? 's' : ''}
                    {eventoPanelExpandido === ev.clave ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                )}
                {ev.personal_asignado.length === 0 && ev.vehiculos_asignados.length === 0 && (
                  <span className="text-xs text-gray-400 italic flex items-center gap-1">
                    <Info className="w-3 h-3" />Sin personal/vehículo asignado
                  </span>
                )}
              </div>
            </div>
            
            {/* Menús expandidos */}
            {eventoPanelExpandido === ev.clave && ev.menus_detalle.length > 0 && (
              <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2">
                {ev.menus_detalle.map((m, idx) => (
                  <span key={m.nombre || idx} className="bg-blue-50 text-blue-800 text-xs px-2 py-1 rounded border border-blue-100">
                    {m.nombre} <span className="text-blue-500">({m.num_personas}p)</span>
                  </span>
                ))}
              </div>
            )}
            
            {/* Observaciones */}
            {ev.observaciones && (
              <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500 italic truncate" title={ev.observaciones}>
                {ev.observaciones}
              </div>
            )}
          </div>
        );
      })()}

      {/* SECCIÓN: TAREAS */}
      {seccionActual === 'tareas' && (
      <>
      {/* Alertas de carga - recalculadas por evento seleccionado */}
      {(() => {
        const resumenLocal = { "-2": 0, "-1": 0, "0": 0, "+1": 0 };
        tareasFiltradas.filter(t => t.estado !== 'completada').forEach(t => {
          const fase = t.fase || "0";
          if (fase in resumenLocal) resumenLocal[fase] += t.tiempo_estimado_min || 0;
        });
        const fN = {
          "-2": "Preparación de material (-2 días)",
          "-1": "Preproducción / Descongelado (-1 día)",
          "0": "Producción del día (día 0)",
          "+1": "Postevento / Limpieza (+1 día)"
        };
        const avisosLocales = Object.entries(resumenLocal)
          .filter(([, t]) => t > 120)
          .map(([f, t]) => `${fN[f]}: ${t} min - Requiere 2 personas o empezar antes`);
        
        if (avisosLocales.length === 0) return null;
        return (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="font-bold text-red-800">Alertas de Carga</span>
                {avisosLocales.map((aviso, idx) => (
                  <p key={`aviso-${idx}-${aviso.slice(0, 20)}`} className="text-red-700">{aviso}</p>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Panel de Planificación del Día - Colapsable */}
      {(() => {
        // Recalcular resumen de carga basado en tareas filtradas (por evento seleccionado)
        const resumenFiltrado = { "-2": 0, "-1": 0, "0": 0, "+1": 0 };
        tareasFiltradas.filter(t => t.estado !== 'completada').forEach(t => {
          const fase = t.fase || "0";
          if (fase in resumenFiltrado) resumenFiltrado[fase] += t.tiempo_estimado_min || 0;
        });
        const tiempoTotalMin = Object.values(resumenFiltrado).reduce((a, b) => a + b, 0);
        
        if (tiempoTotalMin === 0) return null;
        
        const tiempoConPersonas = Math.ceil(tiempoTotalMin / personasTrabajando);
        const margenMin = 30;
        const tiempoNecesario = tiempoConPersonas + margenMin;
        const [horaS, minS] = horaSalida.split(':').map(Number);
        const salidaEnMin = horaS * 60 + minS;
        const llegadaEnMin = salidaEnMin - tiempoNecesario;
        const horaLlegada = `${String(Math.floor(Math.max(0, llegadaEnMin) / 60)).padStart(2, '0')}:${String(Math.max(0, llegadaEnMin) % 60).padStart(2, '0')}`;
        const esTiempoAjustado = tiempoConPersonas > 180;

        return (
          <div className="bg-white rounded-xl shadow-sm mb-3 overflow-hidden">
            {/* Barra compacta siempre visible */}
            <div 
              className={`px-3 py-2 flex items-center justify-between cursor-pointer transition-all ${
                esTiempoAjustado ? 'bg-red-50' : 'bg-green-50'
              }`}
              onClick={() => setPanelPlanificacionExpandido(!panelPlanificacionExpandido)}
            >
              <div className="flex items-center gap-6">
                <span className="text-lg font-bold text-gray-800">
                  ⏱️ {tiempoTotalMin} min total
                </span>
                <span className="text-gray-500">→</span>
                <span className={`text-lg font-bold ${esTiempoAjustado ? 'text-red-700' : 'text-blue-700'}`}>
                  👥 {personasTrabajando}p = {tiempoConPersonas} min
                </span>
                <span className="text-gray-500">→</span>
                <span className={`text-xl font-bold ${esTiempoAjustado ? 'text-red-700 animate-pulse' : 'text-green-700'}`}>
                  ⏰ Llegar: {horaLlegada}
                </span>
                <span className="text-sm text-gray-500">(🚚 {horaSalida})</span>
              </div>
              <div className="flex items-center gap-3">
                {esTiempoAjustado && (
                  <span className="text-red-600 text-sm font-medium">⚠️ Tiempo ajustado</span>
                )}
                <button className="text-gray-500 hover:text-gray-700 p-1">
                  {panelPlanificacionExpandido ? '▲' : '▼'}
                </button>
              </div>
            </div>

            {/* Panel expandido */}
            {panelPlanificacionExpandido && (
              <div className="p-4 border-t bg-white">
                {/* Controles */}
                <div className="flex items-center justify-end gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">👥 Personas:</span>
                    <select 
                      value={personasTrabajando}
                      onChange={(e) => setPersonasTrabajando(parseInt(e.target.value))}
                      className="border rounded-lg px-3 py-2 font-bold bg-blue-50"
                    >
                      {[1,2,3,4,5,6,7,8].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">🚚 Salida:</span>
                    <input 
                      type="time"
                      value={horaSalida}
                      onChange={(e) => setHoraSalida(e.target.value)}
                      className="border rounded-lg px-3 py-2 font-bold bg-yellow-50"
                    />
                  </div>
                </div>
                
                {/* Resumen por fase */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {Object.entries(resumenFiltrado).map(([fase, minutos]) => (
                    <div key={fase} className={`${getFaseBgColor(fase)} rounded-lg p-3 border ${
                      minutos > 120 ? 'border-red-400' : 'border-gray-200'
                    }`}>
                      <div className={`${getFaseColor(fase)} text-white text-xs px-2 py-0.5 rounded mb-1 inline-block`}>
                        {getFaseLabel(fase)}
                      </div>
                      <div className="text-2xl font-bold text-gray-900">{minutos} min</div>
                    </div>
                  ))}
                </div>
                
                {/* Cálculo detallado */}
                <div className={`rounded-lg p-3 ${esTiempoAjustado ? 'bg-red-100' : 'bg-green-100'}`}>
                  <div className="grid grid-cols-4 gap-4 text-center text-sm">
                    <div>
                      <div className="text-gray-600">Total</div>
                      <div className="text-xl font-bold">{tiempoTotalMin} min</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Con {personasTrabajando}p</div>
                      <div className="text-xl font-bold text-blue-700">{tiempoConPersonas} min</div>
                    </div>
                    <div>
                      <div className="text-gray-600">🚚 Salida</div>
                      <div className="text-xl font-bold text-yellow-700">{horaSalida}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">⏰ Llegada</div>
                      <div className={`text-2xl font-bold ${esTiempoAjustado ? 'text-red-700' : 'text-green-700'}`}>{horaLlegada}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-duetto-primary border-t-transparent"></div>
          <p className="mt-4 text-gray-600 text-lg">Cargando tareas...</p>
        </div>
      ) : tareasAgrupadasGlobal.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Sin tareas programadas</h2>
          <p className="text-gray-600">
            {eventoSeleccionado === 'todos' 
              ? 'No hay tareas para el período seleccionado'
              : 'No hay tareas para este evento en el período seleccionado'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* LISTA DE TAREAS AGRUPADAS GLOBALMENTE */}
          {tareasAgrupadasGlobal.map((grupo) => {
            const tareasPendientes = grupo.subtareas.filter(t => t.estado === 'pendiente');
            const hayAtrasadas = grupo.atrasadas > 0;
            
            // Función para formatear fecha corta
            const formatFechaCorta = (fecha) => {
              if (!fecha) return '';
              const d = new Date(fecha);
              const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
              return `${dias[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
            };

            return (
              <div 
                key={grupo.key}
                className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 ${
                  hayAtrasadas ? 'border-red-400' : 
                  grupo.estadoGrupo === 'en_proceso' ? 'border-orange-400' :
                  grupo.estadoGrupo === 'completada' ? 'border-green-400' : 'border-gray-200'
                }`}
              >
                {/* Header del grupo */}
                <div className={`p-4 ${hayAtrasadas ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      {hayAtrasadas && (
                        <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded-full">
                          ⚠️ {grupo.atrasadas} ATRASADA{grupo.atrasadas > 1 ? 'S' : ''}
                        </span>
                      )}
                      <h3 className="text-2xl font-bold text-gray-900 mt-1">{grupo.nombre_tarea}</h3>
                      <p className="text-base text-gray-500">{grupo.plato_nombre}</p>
                    </div>
                  </div>

                  {/* Métricas compactas */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-white rounded-lg p-3 text-center border">
                      <div className="text-xs text-gray-500">Eventos</div>
                      <div className="text-xl font-bold text-purple-600">{grupo.subtareas.length}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border">
                      <div className="text-xs text-gray-500">Total Uds</div>
                      <div className="text-xl font-bold text-blue-600">{grupo.unidades_totales} uds</div>
                      <div className="text-xs text-gray-400">{grupo.lotes_totales} lotes ({grupo.lote_unidades} uds/lote)</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border">
                      <div className="text-xs text-gray-500">Tiempo</div>
                      <div className="text-xl font-bold text-orange-600">{grupo.tiempo_estimado_total} min</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border">
                      <div className="text-xs text-gray-500">Progreso</div>
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-green-600 font-bold">{grupo.completadas}✓</span>
                        <span className="text-orange-500 font-bold">{grupo.en_proceso}⏳</span>
                        <span className="text-gray-400 font-bold">{grupo.pendientes}○</span>
                      </div>
                    </div>
                  </div>

                  {/* Botón COMENZAR TODAS */}
                  {tareasPendientes.length > 1 && (
                    <button
                      onClick={() => {
                        handleAbrirModalAsignar({ 
                          ...grupo, 
                          tareasIds: tareasPendientes.map(t => t.id),
                          modoTodas: true 
                        });
                      }}
                      className="w-full py-4 mb-4 bg-duetto-primary hover:bg-green-600 text-white rounded-xl font-bold text-xl flex items-center justify-center gap-2 active:scale-98 transition-all shadow-md"
                    >
                      <Play className="w-6 h-6" />
                      🚀 COMENZAR TODAS ({tareasPendientes.length})
                    </button>
                  )}

                  {/* Lista de eventos - UNA LÍNEA POR EVENTO */}
                  <div className="space-y-2">
                    {grupo.subtareas.map(tarea => {
                      const personasActivas = (tarea.personas_asignadas || []).filter(p => !p.tiempo_fin);
                      const tiempoInfo = tiemposRestantes[tarea.id];

                      return (
                        <div
                          key={tarea.id}
                          className={`flex items-center gap-2 p-3 rounded-lg transition-all ${
                            tarea.estado === 'completada' 
                              ? 'bg-green-100 border border-green-300' 
                              : tarea.estado === 'en_proceso' 
                                ? 'bg-orange-100 border border-orange-300' 
                                : tarea.esAtrasada
                                  ? 'bg-red-50 border border-red-200'
                                  : tarea.esDeHoy
                                    ? 'bg-yellow-50 border border-yellow-200'
                                    : 'bg-white border border-gray-200'
                          }`}
                        >
                          {/* Estado visual */}
                          <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
                            tarea.estado === 'completada' ? 'bg-green-500' :
                            tarea.estado === 'en_proceso' ? 'bg-orange-500 animate-pulse' :
                            tarea.esAtrasada ? 'bg-red-500' : 
                            tarea.esDeHoy ? 'bg-yellow-500' : 'bg-gray-300'
                          }`} />
                          
                          {/* Badge de estado */}
                          {tarea.esAtrasada && tarea.estado !== 'completada' && (
                            <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded flex-shrink-0">
                              ATRASADA
                            </span>
                          )}
                          
                          {/* Fecha prevista (tiene en cuenta la fase: D-1, D, D+1) */}
                          <span className={`text-sm w-24 flex-shrink-0 flex items-center gap-1 ${
                            tarea.esAtrasada || tarea.esDeHoy ? 'font-bold text-red-700' : 'text-gray-500'
                          }`} title={tarea.jornada_etiqueta ? `${tarea.jornada_etiqueta} del evento` : undefined}>
                            {formatFechaCorta(tarea.fecha_prevista || tarea.fecha_evento)}
                            {tarea.fase && tarea.fase !== '0' && (
                              <span className="text-[10px] text-gray-400">D{tarea.fase > 0 ? '+' : ''}{tarea.fase}</span>
                            )}
                          </span>
                          
                          {/* # Evento y Cliente */}
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <span className="font-bold text-gray-800">#{tarea.presupuesto_numero}</span>
                            <span className="text-gray-600 truncate">{tarea.cliente_nombre}</span>
                          </div>
                          
                          {/* Desglose de lotes - ej: "1x40 + 1x10" */}
                          <span className="text-sm font-bold text-blue-600 flex-shrink-0" data-testid="lote-desglose">
                            {formatDesgloseLotes(tarea)}
                          </span>
                          
                          {/* Indicador de recurso con tiempo fijo */}
                          {tarea.tipo_tiempo_pasivo === 'fijo' && tarea.nombre_recurso && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex-shrink-0" title={`${tarea.nombre_recurso}: ${tarea.capacidad_recurso} uds/tanda`}>
                              {tarea.nombre_recurso}
                            </span>
                          )}
                          
                          {/* Tiempo estimado (solo si no está en proceso) */}
                          {tarea.estado !== 'en_proceso' && (
                            <span className="text-sm font-bold text-orange-600 w-16 text-right flex-shrink-0">
                              {tarea.tiempo_estimado_min} min
                            </span>
                          )}
                          
                          {/* Tiempo transcurrido / restante (cuando está en proceso) */}
                          {tarea.estado === 'en_proceso' && tiempoInfo && (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Tiempo transcurrido */}
                              <span className={`text-sm font-bold px-2 py-1 rounded ${
                                tiempoInfo.excedido ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                ⏱️ {tiempoInfo.transcurrido}m
                              </span>
                              {/* Tiempo restante o excedido */}
                              <span className={`text-sm font-bold px-2 py-1 rounded ${
                                tiempoInfo.excedido ? 'bg-red-200 text-red-800' : 'bg-green-100 text-green-700'
                              }`}>
                                {tiempoInfo.excedido 
                                  ? `+${Math.abs(tiempoInfo.restante)}m ⚠️` 
                                  : `${tiempoInfo.restante}m ⏳`
                                }
                              </span>
                            </div>
                          )}
                          
                          {/* Tiempo estimado cuando está en proceso pero no hay tiempoInfo */}
                          {tarea.estado === 'en_proceso' && !tiempoInfo && (
                            <span className="text-sm font-bold text-orange-600 w-16 text-right flex-shrink-0">
                              {tarea.tiempo_estimado_min} min
                            </span>
                          )}
                          
                          {/* Personas trabajando */}
                          {personasActivas.length > 0 && (
                            <div className="flex -space-x-1 flex-shrink-0">
                              {personasActivas.slice(0,2).map((p, idx) => {
                                const colorClasses = p.color ? getColorClasses(p.color) : { bg: 'bg-blue-500' };
                                return (
                                  <div 
                                    key={p.id || p.nombre || idx} 
                                    className={`w-7 h-7 rounded-full ${colorClasses.bg} flex items-center justify-center text-white text-xs font-bold border-2 border-white`}
                                    title={p.nombre}
                                  >
                                    {p.nombre?.charAt(0).toUpperCase()}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Botón de acción - MÁS GRANDE */}
                          {tarea.estado === 'completada' ? (
                            <span className="text-green-600 font-bold text-xl flex-shrink-0 px-2">✓</span>
                          ) : tarea.estado === 'en_proceso' ? (
                            <button
                              onClick={() => {
                                if (personasActivas[0]) {
                                  handleFinalizarPersona(tarea.id, personasActivas[0].empleado_id);
                                }
                              }}
                              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold active:scale-95 flex-shrink-0"
                            >
                              ✓ Hecho
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAbrirModalAsignar(tarea)}
                              className={`${
                                tarea.esAtrasada 
                                  ? 'bg-red-500 hover:bg-red-600' 
                                  : 'bg-duetto-primary hover:bg-green-600'
                              } text-white px-5 py-2.5 rounded-xl text-sm font-bold active:scale-95 flex items-center gap-1 flex-shrink-0`}
                            >
                              <Play className="w-4 h-4" />
                              Comenzar
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Ingredientes/materiales - detalle por lote */}
                  {(grupo.ingredientes.length > 0 || grupo.materiales.length > 0) && (
                    <details className="mt-3" data-testid="ingredientes-materiales-detail">
                      <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700 py-2">
                        📦 Ver ingredientes y materiales
                      </summary>
                      
                      {/* Vista por evento individual con lotes asignados */}
                      <div className="space-y-3 mt-2">
                        {grupo.subtareas.map((tarea, tIdx) => {
                          const taskIngs = tarea.ingredientes || [];
                          const taskMats = tarea.materiales || [];
                          if (taskIngs.length === 0 && taskMats.length === 0) return null;
                          
                          return (
                            <div key={tIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                              {/* Encabezado del evento */}
                              <div className="bg-gray-50 px-3 py-2 flex items-center gap-2 text-sm">
                                <span className="font-bold text-gray-700">#{tarea.presupuesto_numero}</span>
                                <span className="text-gray-500 truncate">{tarea.cliente_nombre}</span>
                                <span className="text-blue-600 font-semibold ml-auto">{tarea.unidades_totales} uds</span>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                                {/* Ingredientes con lotes */}
                                {taskIngs.length > 0 && (
                                  <div className="p-3 border-r border-gray-100">
                                    <div className="font-bold text-green-800 mb-2 text-xs uppercase tracking-wide">Ingredientes</div>
                                    <div className="space-y-2">
                                      {taskIngs.map((ing, iIdx) => (
                                        <div key={iIdx} className="text-sm">
                                          <div className="flex justify-between text-gray-700">
                                            <span className="font-medium">{ing.nombre}</span>
                                            <span className="font-bold">{Math.round(ing.cantidad_total * 10) / 10} {ing.unidad}</span>
                                          </div>
                                          {/* Lotes asignados */}
                                          {ing.lotes_asignados && ing.lotes_asignados.length > 0 && (
                                            <div className="ml-2 mt-1 space-y-0.5">
                                              {ing.lotes_asignados.map((lote, lIdx) => {
                                                const cadStr = lote.fecha_caducidad || '';
                                                const cadDate = cadStr ? new Date(cadStr) : null;
                                                const hoy = new Date();
                                                const diasHastaCad = cadDate ? Math.ceil((cadDate - hoy) / (1000*60*60*24)) : null;
                                                const cadProxima = diasHastaCad !== null && diasHastaCad < 30;
                                                
                                                return (
                                                  <div key={lIdx} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${
                                                    cadProxima ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
                                                  }`} data-testid="lote-asignado">
                                                    <span className="font-semibold text-amber-800">Lote: {lote.numero_lote}</span>
                                                    <span className="text-gray-500">|</span>
                                                    <span className="font-medium">{lote.cantidad} {ing.unidad}</span>
                                                    <span className="text-gray-500">|</span>
                                                    <span className={`${cadProxima ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
                                                      Cad: {cadStr ? new Date(cadStr).toLocaleDateString('es-ES', {day:'numeric', month:'short', year:'2-digit'}) : '?'}
                                                    </span>
                                                    {cadProxima && <span className="text-red-500 font-bold">!</span>}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                          {(!ing.lotes_asignados || ing.lotes_asignados.length === 0) && ing.ingrediente_id && (
                                            <div className="ml-2 mt-0.5 text-xs text-gray-400 italic">Sin lote reservado</div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Materiales */}
                                {taskMats.length > 0 && (
                                  <div className="p-3">
                                    <div className="font-bold text-blue-800 mb-2 text-xs uppercase tracking-wide">Material</div>
                                    <div className="space-y-1">
                                      {taskMats.map((mat, mIdx) => (
                                        <div key={mIdx} className="flex justify-between text-sm text-gray-700">
                                          <span>{mat.nombre}</span>
                                          <span className="font-medium">{Math.round(mat.cantidad * 10) / 10} {mat.unidad}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Total consolidado */}
                        {grupo.subtareas.length > 1 && (
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <div className="font-bold text-gray-700 text-xs uppercase tracking-wide mb-2">Total consolidado</div>
                            <div className="grid grid-cols-2 gap-3">
                              {grupo.ingredientes.length > 0 && (
                                <div className="space-y-1">
                                  {grupo.ingredientes.map((ing, idx) => (
                                    <div key={idx} className="flex justify-between text-sm text-gray-700">
                                      <span>{ing.nombre}</span>
                                      <span className="font-bold">{Math.round(ing.cantidad_total * 10) / 10} {ing.unidad}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {grupo.materiales.length > 0 && (
                                <div className="space-y-1">
                                  {grupo.materiales.map((mat, idx) => (
                                    <div key={idx} className="flex justify-between text-sm text-gray-700">
                                      <span>{mat.nombre}</span>
                                      <span className="font-bold">{Math.round(mat.cantidad * 10) / 10} {mat.unidad}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            );
          })}

        </div>
      )}
      </>
      )}
      {/* FIN SECCIÓN: TAREAS */}

      {/* SECCIÓN: RESUMEN DE EVENTOS */}
      {seccionActual === 'resumen' && (
        <div className="space-y-6">
          {/* Dashboard de Eventos - Vista Rápida */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="w-7 h-7 mr-2 text-duetto-primary" />
              Estado de Eventos
            </h2>
            
            {estadisticasEventos.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📅</div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">Sin eventos programados</h3>
                <p className="text-gray-500">No hay tareas para el período seleccionado</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {estadisticasEventos.map(evento => {
                  const progreso = evento.total > 0 ? (evento.completadas / evento.total) * 100 : 0;
                  const fechaEvento = evento.fecha_evento ? new Date(evento.fecha_evento) : null;
                  const horaEvento = fechaEvento ? fechaEvento.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';
                  const fechaStr = fechaEvento ? fechaEvento.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '';
                  
                  // Determinar color según progreso
                  let statusColor = 'border-gray-300 bg-gray-50';
                  let progressBarColor = 'bg-gray-400';
                  if (progreso === 100) {
                    statusColor = 'border-green-400 bg-green-50';
                    progressBarColor = 'bg-green-500';
                  } else if (progreso >= 50) {
                    statusColor = 'border-blue-400 bg-blue-50';
                    progressBarColor = 'bg-blue-500';
                  } else if (progreso > 0) {
                    statusColor = 'border-yellow-400 bg-yellow-50';
                    progressBarColor = 'bg-yellow-500';
                  }
                  
                  return (
                    <div
                      key={evento.clave}
                      className={`border-2 ${statusColor} rounded-xl p-4 transition-all hover:shadow-lg cursor-pointer`}
                      onClick={() => {
                        setSeccionActual('tareas');
                        setEventoSeleccionado(evento.clave);
                      }}
                    >
                      {/* Header con fecha/hora */}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-gray-500">{fechaStr}</span>
                        <span className="text-xs font-bold text-duetto-primary">{horaEvento}</span>
                      </div>
                      
                      {/* Nombre cliente */}
                      <h3 className="font-bold text-gray-900 text-lg truncate mb-1">
                        {evento.cliente_nombre}
                      </h3>
                      <p className="text-xs text-gray-500 mb-1">
                        #{evento.presupuesto_numero}{evento.jornada_etiqueta ? ` · ${evento.jornada_etiqueta}` : ''}
                      </p>
                      
                      {/* Info compacta del evento */}
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-gray-500 mb-3">
                        {evento.hora_inicio && (
                          <span className="font-semibold text-blue-600">{evento.hora_inicio}{evento.hora_fin ? `-${evento.hora_fin}` : ''}</span>
                        )}
                        {evento.num_personas > 0 && (
                          <span>{evento.num_personas}p</span>
                        )}
                        {evento.personal_asignado.length > 0 && (
                          <span className="text-green-600">{evento.personal_asignado.map(p => p.nombre).join(', ')}</span>
                        )}
                        {evento.vehiculos_asignados.length > 0 && (
                          <span className="text-amber-600">{evento.vehiculos_asignados.map(v => v.nombre || v.matricula).join(', ')}</span>
                        )}
                      </div>
                      
                      {/* Progreso circular visual */}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-3xl font-bold text-gray-900">{progreso.toFixed(0)}%</span>
                        <div className="text-right text-xs">
                          <div className="text-green-600 font-bold">{evento.completadas} ✓</div>
                          <div className="text-orange-500">{evento.en_proceso} ⏳</div>
                          <div className="text-gray-500">{evento.pendientes} 📋</div>
                        </div>
                      </div>
                      
                      {/* Barra de progreso */}
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`${progressBarColor} h-3 rounded-full transition-all duration-500`}
                          style={{ width: `${progreso}%` }}
                        />
                      </div>
                      
                      {/* Indicador de estado */}
                      <div className="mt-2 text-center">
                        {progreso === 100 ? (
                          <span className="text-xs font-bold text-green-600">✓ COMPLETADO</span>
                        ) : evento.en_proceso > 0 ? (
                          <span className="text-xs font-bold text-orange-500">⏳ EN PROCESO</span>
                        ) : (
                          <span className="text-xs font-bold text-gray-500">📋 PENDIENTE</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sección: Preparaciones Unificadas */}
          {estadisticasEventos.length > 0 && (() => {
            // Agrupar tareas por nombre de plato exacto
            const preparacionesAgrupadas = {};
            
            tareas.forEach(tarea => {
              if (tarea.estado === 'completada') return; // Ignorar completadas
              
              const key = tarea.plato_nombre; // Agrupar por nombre exacto
              if (!key) return;
              
              if (!preparacionesAgrupadas[key]) {
                preparacionesAgrupadas[key] = {
                  plato_nombre: tarea.plato_nombre,
                  nombre_tarea: tarea.nombre_tarea,
                  eventos: [],
                  total_cantidad: 0
                };
              }
              
              // Buscar si ya existe este evento
              // (en multievento, cada jornada es un evento)
              const eventoExistente = preparacionesAgrupadas[key].eventos.find(
                e => e.clave === claveEventoTablet(tarea)
              );
              
              // Unidades de la tarea operativa (U-65: antes contaba 1 por tarea)
              const unidadesTarea = Number(tarea.unidades_totales) || 0;
              if (eventoExistente) {
                eventoExistente.cantidad += unidadesTarea;
                eventoExistente.tareas.push(tarea);
              } else {
                // Hora del servicio real (la del evento o la jornada), no la
                // medianoche de fecha_evento
                const infoEvento = eventosInfo[claveEventoTablet(tarea)] || eventosInfo[tarea.presupuesto_id] || {};
                preparacionesAgrupadas[key].eventos.push({
                  clave: claveEventoTablet(tarea),
                  jornada_etiqueta: tarea.jornada_etiqueta || '',
                  presupuesto_id: tarea.presupuesto_id,
                  presupuesto_numero: tarea.presupuesto_numero,
                  cliente_nombre: tarea.cliente_nombre,
                  fecha_evento: infoEvento.fecha_evento || tarea.fecha_evento,
                  hora_evento: infoEvento.hora_inicio || '',
                  cantidad: unidadesTarea,
                  tareas: [tarea]
                });
              }

              preparacionesAgrupadas[key].total_cantidad += unidadesTarea;
            });
            
            // Filtrar solo los que tienen más de 1 evento
            const preparacionesMultiples = Object.values(preparacionesAgrupadas)
              .filter(p => p.eventos.length > 1)
              .sort((a, b) => b.eventos.length - a.eventos.length);
            
            if (preparacionesMultiples.length === 0) return null;
            
            return (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                  <h2 className="text-2xl font-bold text-white flex items-center">
                    🔗 Preparaciones Unificadas
                  </h2>
                  <p className="text-purple-100 text-sm">
                    Platos que se repiten en varios eventos - Optimiza preparando juntos
                  </p>
                </div>
                
                <div className="p-6 space-y-4">
                  {preparacionesMultiples.map((prep, idx) => (
                    <div key={idx} className="border-2 border-purple-200 rounded-xl overflow-hidden">
                      {/* Header del plato */}
                      <div className="bg-purple-50 px-4 py-3 flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-purple-900">{prep.plato_nombre}</h3>
                          <p className="text-sm text-purple-600">{prep.nombre_tarea}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-purple-700">{prep.total_cantidad}</div>
                          <div className="text-xs text-purple-500">unidades total</div>
                        </div>
                      </div>
                      
                      {/* Desglose por evento */}
                      <div className="px-4 py-3 space-y-2">
                        {prep.eventos
                          .sort((a, b) => new Date(a.fecha_evento) - new Date(b.fecha_evento))
                          .map((evento, evIdx) => {
                            const esPrioritario = evIdx === 0;
                            return (
                              <div
                                key={evento.clave}
                                className={`flex items-center justify-between p-3 rounded-lg ${
                                  esPrioritario ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center">
                                  {esPrioritario && <span className="text-yellow-500 mr-2">⭐</span>}
                                  <div>
                                    <span className="font-bold text-gray-900">{evento.cliente_nombre}</span>
                                    <span className="text-xs text-gray-500 ml-2">#{evento.presupuesto_numero}{evento.jornada_etiqueta ? ` · ${evento.jornada_etiqueta}` : ''}</span>
                                    <div className="text-xs text-gray-500">
                                      📅 {new Date(evento.fecha_evento).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                      {evento.hora_evento ? ` · ${evento.hora_evento}` : ''}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl font-bold text-gray-700">{evento.cantidad}</span>
                                  <button
                                    onClick={async () => {
                                      // Completar solo las tareas de este evento
                                      const { fallos } = await completarTareasOperativas(evento.tareas);
                                      setToastMessage(fallos > 0
                                        ? { error: true, texto: `No se han podido completar ${fallos} de ${evento.tareas.length} tareas de ${prep.plato_nombre}. Revisa la conexión y vuelve a intentarlo.` }
                                        : `${evento.cantidad} ${prep.plato_nombre} completados para ${evento.cliente_nombre}`);
                                      setTimeout(() => setToastMessage(null), fallos > 0 ? 5000 : 3000);
                                      fetchTareas();
                                    }}
                                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-bold text-sm transition-all"
                                  >
                                    ✓ Completar ({evento.cantidad})
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      
                      {/* Botón para completar TODAS */}
                      <div className="px-4 py-3 bg-purple-100 border-t border-purple-200">
                        <button
                          onClick={async () => {
                            // Completar todas las tareas de todos los eventos
                            const todas = prep.eventos.flatMap(ev => ev.tareas);
                            const { fallos } = await completarTareasOperativas(todas);
                            setToastMessage(fallos > 0
                              ? { error: true, texto: `No se han podido completar ${fallos} de ${todas.length} tareas de ${prep.plato_nombre}. Revisa la conexión y vuelve a intentarlo.` }
                              : `Todas las ${prep.total_cantidad} ${prep.plato_nombre} completadas para ${prep.eventos.length} eventos`);
                            setTimeout(() => setToastMessage(null), fallos > 0 ? 5000 : 4000);
                            fetchTareas();
                          }}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white px-6 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center"
                        >
                          🚀 PREPARAR TODAS ({prep.total_cantidad} unidades para {prep.eventos.length} eventos)
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Detalle de cada Evento */}
          {estadisticasEventos.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gray-800 px-6 py-4">
                <h2 className="text-xl font-bold text-white">📋 Detalle por Evento</h2>
              </div>
              <div className="p-4 space-y-4">
                {estadisticasEventos.map(evento => {
                  const progreso = evento.total > 0 ? (evento.completadas / evento.total) * 100 : 0;
                  const tiempoRealVsEstimado = evento.tiempo_total_real > 0 
                    ? ((evento.tiempo_total_real / evento.tiempo_total_estimado) * 100) - 100
                    : 0;
                  
                  return (
                    <div key={evento.clave} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg text-gray-900">{evento.cliente_nombre}</h3>
                          <p className="text-sm text-gray-500">
                            #{evento.presupuesto_numero}{evento.jornada_etiqueta ? ` · ${evento.jornada_etiqueta}` : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-duetto-primary">{progreso.toFixed(0)}%</div>
                          <div className="text-xs text-gray-500">{evento.completadas}/{evento.total} tareas</div>
                        </div>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div 
                          className="bg-duetto-primary h-2 rounded-full"
                          style={{ width: `${progreso}%` }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2 text-center text-xs">
                        <div className="bg-gray-50 rounded p-2">
                          <div className="font-bold text-gray-700">{evento.total}</div>
                          <div className="text-gray-500">Total</div>
                        </div>
                        <div className="bg-green-50 rounded p-2">
                          <div className="font-bold text-green-600">{evento.completadas}</div>
                          <div className="text-gray-500">Listas</div>
                        </div>
                        <div className="bg-orange-50 rounded p-2">
                          <div className="font-bold text-orange-600">{evento.en_proceso}</div>
                          <div className="text-gray-500">En curso</div>
                        </div>
                        <div className="bg-blue-50 rounded p-2">
                          <div className="font-bold text-blue-600">{evento.pendientes}</div>
                          <div className="text-gray-500">Pendientes</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SECCIÓN: FICHAJE */}
      {seccionActual === 'fichaje' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Registro de Entrada/Salida</h2>

            {/* Botones de Fichaje */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <button 
                onClick={() => setShowModalEntrada(true)}
                className="bg-green-500 hover:bg-green-600 text-white rounded-xl p-8 font-bold text-2xl shadow-lg transition-all hover:scale-105 flex flex-col items-center"
              >
                <LogIn className="w-16 h-16 mb-4" />
                ENTRADA
                <span className="text-lg font-normal mt-2">{new Date().toLocaleTimeString('es-ES')}</span>
              </button>
              <button 
                onClick={() => setShowModalSalida(true)}
                className="bg-red-500 hover:bg-red-600 text-white rounded-xl p-8 font-bold text-2xl shadow-lg transition-all hover:scale-105 flex flex-col items-center"
              >
                <LogOut className="w-16 h-16 mb-4" />
                SALIDA
                <span className="text-lg font-normal mt-2">{new Date().toLocaleTimeString('es-ES')}</span>
              </button>
            </div>

            {/* Mensaje informativo */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
              <p className="text-blue-900">
                <strong>Nota:</strong> Selecciona tu nombre para registrar entrada o salida.
              </p>
            </div>
          </div>

          {/* Historial de Fichajes del Día */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Fichajes de Hoy</h3>
            {fichajesHoy.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No hay fichajes registrados hoy</p>
              </div>
            ) : (
              <div className="space-y-3">
                {fichajesHoy.map((fichaje, idx) => {
                  const colorClasses = fichaje.empleado_color ? getColorClasses(fichaje.empleado_color) : { bg: 'bg-blue-500', bgLight: 'bg-blue-50' };
                  const horaEntrada = fichaje.hora_entrada ? new Date(fichaje.hora_entrada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '-';
                  const horaSalida = fichaje.hora_salida ? new Date(fichaje.hora_salida).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '-';
                  
                  return (
                    <div key={idx} className={`${colorClasses.bgLight} border-2 ${colorClasses.border} rounded-lg p-4`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-12 h-12 rounded-full ${colorClasses.bg} flex items-center justify-center text-white font-bold text-xl mr-3`}>
                            {fichaje.empleado_nombre?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-lg text-gray-900">{fichaje.empleado_nombre}</div>
                            <div className="text-sm text-gray-600">
                              {fichaje.hora_salida ? (
                                <span className="text-green-600 font-medium">✓ Jornada completada</span>
                              ) : (
                                <span className="text-orange-600 font-medium">⏱️ Trabajando</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Entrada: <span className="font-bold">{horaEntrada}</span></div>
                          <div className="text-sm text-gray-600">Salida: <span className="font-bold">{horaSalida}</span></div>
                          {fichaje.minutos_trabajados && (
                            <div className="text-sm text-duetto-primary font-bold mt-1">
                              {Math.floor(fichaje.minutos_trabajados / 60)}h {fichaje.minutos_trabajados % 60}min
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Asignar Persona */}
      {showAsignarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full">
            <div className="bg-gradient-to-r from-duetto-primary to-green-600 px-6 py-4 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-white">¿Quién va a realizar esta tarea?</h2>
              <p className="text-green-100">
                {tareaSeleccionada?.modoTodas 
                  ? `${tareaSeleccionada?.nombre_tarea} (${tareaSeleccionada?.tareasIds?.length} eventos)`
                  : tareaSeleccionada?.nombre_tarea
                }
              </p>
              {tiempoPorDefecto > 0 && (
                <p className="text-green-200 text-sm mt-1">
                  ⏱️ Tiempo estimado por lote: {tiempoPorDefecto} min
                </p>
              )}
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto">
                {empleados.map(emp => {
                  const colorClasses = emp.color ? getColorClasses(emp.color) : { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', text: 'text-blue-900' };
                  const nombrePila = (emp.nombre_completo || emp.nombre || 'Empleado').split(' ')[0];
                  
                  // Obtener estadísticas del empleado para esta tarea
                  const stats = estadisticasEmpleados[emp.id];
                  const tiempoMedio = stats?.tiempo_medio_por_lote;
                  const tareasHechas = stats?.tareas_completadas || 0;
                  
                  // Determinar si es más rápido, normal o más lento que el tiempo por defecto
                  let indicadorVelocidad = null;
                  if (tiempoMedio && tiempoPorDefecto > 0) {
                    const diferencia = ((tiempoMedio - tiempoPorDefecto) / tiempoPorDefecto) * 100;
                    if (diferencia <= -15) {
                      indicadorVelocidad = { emoji: '🚀', text: 'Rápido', color: 'text-green-300' };
                    } else if (diferencia >= 15) {
                      indicadorVelocidad = { emoji: '🐢', text: 'Lento', color: 'text-orange-300' };
                    }
                  }
                  
                  return (
                    <button
                      key={emp.id}
                      onClick={async () => {
                        if (tareaSeleccionada?.modoTodas && tareaSeleccionada?.tareasIds) {
                          // Iniciar todas las tareas
                          for (const tareaId of tareaSeleccionada.tareasIds) {
                            await handleAsignarPersona(emp.id, tareaId);
                          }
                          setShowAsignarModal(false);
                          setTareaSeleccionada(null);
                        } else {
                          handleAsignarPersona(emp.id);
                        }
                      }}
                      className={`${colorClasses.bg} ${colorClasses.hover} text-white rounded-xl px-4 py-6 font-bold text-lg shadow-lg transition-all hover:scale-105 flex flex-col items-center relative`}
                    >
                      {/* Indicador de velocidad */}
                      {indicadorVelocidad && (
                        <span className={`absolute top-2 right-2 text-lg`} title={indicadorVelocidad.text}>
                          {indicadorVelocidad.emoji}
                        </span>
                      )}
                      
                      <div className="w-14 h-14 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-2xl mb-2">
                        {nombrePila.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-lg">{nombrePila}</span>
                      
                      {/* Tiempo medio por lote */}
                      <div className="mt-2 text-center">
                        {tiempoMedio ? (
                          <div className="bg-white bg-opacity-20 rounded-lg px-3 py-1">
                            <span className="text-sm font-bold">{tiempoMedio} min/lote</span>
                            <span className="text-xs block opacity-80">({tareasHechas} tareas)</span>
                          </div>
                        ) : (
                          <div className="bg-white bg-opacity-10 rounded-lg px-3 py-1">
                            <span className="text-sm opacity-70">
                              {tiempoPorDefecto > 0 ? `~${tiempoPorDefecto} min/lote` : 'Sin datos'}
                            </span>
                            <span className="text-xs block opacity-50">Primera vez</span>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => {
                  setShowAsignarModal(false);
                  setTareaSeleccionada(null);
                  setEstadisticasEmpleados({});
                }}
                className="mt-6 w-full bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg px-6 py-3 font-bold transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cambiar Persona */}
      {showCambiarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-white">Cambiar persona asignada</h2>
              <p className="text-orange-100">Actualmente: {personaACambiar?.nombre}</p>
              <p className="text-xs text-orange-100 mt-1">⚠️ No se modificarán los tiempos registrados</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto">
                {empleados.filter(e => e.id !== personaACambiar?.empleado_id).map(emp => {
                  const colorClasses = emp.color ? getColorClasses(emp.color) : { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', text: 'text-blue-900' };
                  return (
                    <button
                      key={emp.id}
                      onClick={() => handleCambiarPersona(emp.id)}
                      className={`${colorClasses.bg} ${colorClasses.hover} text-white rounded-xl px-6 py-8 font-bold text-xl shadow-lg transition-all hover:scale-105 flex flex-col items-center`}
                    >
                      <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-3xl mb-3">
                        {emp.nombre_completo?.charAt(0).toUpperCase()}
                      </div>
                      {emp.nombre_completo || emp.nombre || emp.email}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => {
                  setShowCambiarModal(false);
                  setTareaSeleccionada(null);
                  setPersonaACambiar(null);
                }}
                className="mt-6 w-full bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg px-6 py-3 font-bold transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fichar Entrada */}
      {showModalEntrada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full">
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-white">🟢 Registrar Entrada</h2>
              <p className="text-green-100">Selecciona tu nombre para fichar entrada</p>
            </div>
            <div className="p-6">
              {getEmpleadosParaEntrada().length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 text-lg">Todos los empleados ya han fichado entrada</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto">
                  {getEmpleadosParaEntrada().map(emp => {
                    const colorClasses = emp.color ? getColorClasses(emp.color) : { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', text: 'text-blue-900' };
                    return (
                      <button
                        key={emp.id}
                        onClick={() => handleFicharEntrada(emp.id)}
                        className={`${colorClasses.bg} ${colorClasses.hover} text-white rounded-xl px-6 py-8 font-bold text-xl shadow-lg transition-all hover:scale-105 flex flex-col items-center`}
                      >
                        <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-3xl mb-3">
                          {emp.nombre_completo?.charAt(0).toUpperCase()}
                        </div>
                        {emp.nombre_completo || emp.nombre || emp.email}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                onClick={() => setShowModalEntrada(false)}
                className="mt-6 w-full bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg px-6 py-3 font-bold transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fichar Salida */}
      {showModalSalida && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-white">🔴 Registrar Salida</h2>
              <p className="text-red-100">Selecciona tu nombre para fichar salida</p>
            </div>
            <div className="p-6">
              {getEmpleadosParaSalida().length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 text-lg">No hay empleados trabajando actualmente</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto">
                  {getEmpleadosParaSalida().map(emp => {
                    const colorClasses = emp.color ? getColorClasses(emp.color) : { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', text: 'text-blue-900' };
                    return (
                      <button
                        key={emp.id}
                        onClick={() => handleFicharSalida(emp.id)}
                        className={`${colorClasses.bg} ${colorClasses.hover} text-white rounded-xl px-6 py-8 font-bold text-xl shadow-lg transition-all hover:scale-105 flex flex-col items-center`}
                      >
                        <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-3xl mb-3">
                          {emp.nombre_completo?.charAt(0).toUpperCase()}
                        </div>
                        {emp.nombre_completo || emp.nombre || emp.email}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                onClick={() => setShowModalSalida(false)}
                className="mt-6 w-full bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg px-6 py-3 font-bold transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
      {/* Fin Contenido principal */}

      {/* Toast Notification */}
      {/* Texto (aviso verde) o {texto, error: true} (aviso rojo, sin el check) */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-[60] animate-fade-in">
          <div className={`${toastMessage.error ? 'bg-red-600' : 'bg-green-500'} text-white px-6 py-4 rounded-xl shadow-2xl flex items-center space-x-3`}>
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              {toastMessage.error ? <AlertCircle className="w-5 h-5" /> : <CheckSquare className="w-5 h-5" />}
            </div>
            <p className="font-bold text-lg">{toastMessage.error ? toastMessage.texto : toastMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabletCocina;
